import { Injectable } from '@angular/core';
import { CanActivate } from '@angular/router';
import { Observable, Observer, of } from 'rxjs';
import { catchError, concatAll, concatMap, map, tap, withLatestFrom } from 'rxjs/operators';
import { APIService } from 'src/app/core/api/general.service';
import { LOCALSTORAGEVARS, OptionType, PaymentType } from 'src/app/core/enums/enums';
import { Location, QuoteRequest } from 'src/app/core/models/itinerary.model';
import { ExchangeSegment } from 'src/app/core/models/voluntary-change.model';
import { LayoutService } from 'src/app/layout/services/layout.service';
import { SettingsService } from 'src/app/settings/services/settings.service';
import { VoluntaryChangeService } from 'src/app/voluntary-change/services/voluntary-change.service';
import { ItineraryService } from '../../search-result/services/itinerary.service';
import { PaymentMethod } from 'src/app/core/models/reissue.model';
import { TranslateService } from '@ngx-translate/core';
import { ReissueService } from '../services/reissue.service';

@Injectable()
export class ReissueGuard implements CanActivate {
  constructor(
    private api: APIService,
    private itineraryService: ItineraryService,
    private layoutService: LayoutService,
    private service: VoluntaryChangeService,
    private settingsService: SettingsService,
    private translate: TranslateService,
    private reissueService: ReissueService
  ) {}

  canActivate(): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      this.itineraryService
        .getQuoteRequest()
        .pipe(
          map(this.loadVoluntaryChange.bind(this, observer)),
          concatAll(),
          tap((approved: boolean) =>
            approved ? this.approvedVisit(observer) : this.rejectedVisit(observer)
          ),
          catchError(this.rejectedVisit.bind(this, observer))
        )
        .subscribe();
    });
  }

  private loadVoluntaryChange(
    observer: Observer<boolean>,
    quoteRequest: QuoteRequest
  ): Observable<boolean> {
    return this.api.getVoluntaryQuote(quoteRequest).pipe(
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.settingsService.getClientSettings(),
            this.settingsService.getClientIsAirline()
          )
        )
      ),
      map(([response, settings, isAirline]) => {
        this.reissueService.dispatchLoadPaymentMethods(
          this.getPaymentMethods(response.formOfPaymentTypes)
        );
        this.layoutService.dispatchChangeBreadcrumb(quoteRequest.pnr, OptionType.Reissue);
        response.exchangeSegments = this.mapSegments(response.exchangeSegments);
        this.service.dispatchLoadVoluntaryQuoteSuccess(
          response,
          quoteRequest,
          settings.reissue.isVoluntary,
          isAirline,
          true,
          JSON.parse(window.localStorage.getItem(LOCALSTORAGEVARS.redirectFromManual)) === true
        );
        return response && settings.reissue.isVoluntary;
      }),
      catchError((response) => {
        this.itineraryService.dispatchGenericError(response.error);
        return this.rejectedVisit(observer);
      })
    );
  }

  private rejectedVisit(observer: Observer<boolean>): Observable<boolean> {
    this.itineraryService.navigateToWithParams('/itinerary');
    observer.next(false);
    observer.complete();
    return of(false);
  }

  private approvedVisit(observer: Observer<boolean>): void {
    observer.next(true);
    observer.complete();
  }

  private mapSegments(segment: ExchangeSegment[]): ExchangeSegment[] {
    return segment.map((flight) => ({
      ...flight,
      departure: this.addLabelToLocation(flight.departure),
      arrival: this.addLabelToLocation(flight.arrival)
    }));
  }

  private addLabelToLocation(location: Location): Location {
    return {
      ...location,
      label: `${location.code ? location.code : ''}  ${
        location.name ? ' - ' + location.name : ''
      }  ${location.airportName ? ' - ' + location.airportName : ''}`
    };
  }

  private getPaymentMethods(methods: string[]): PaymentMethod[] {
    return methods.map((method) => ({
      id: method as PaymentType,
      label: this.translate.instant(`reissue.payment-methods.${method}`),
      isChecked: false
    }));
  }
}
